import { createReducer, on } from '@ngrx/store';

import * as DashboardActions from './dashboard.actions';
import { Dashboard, DashboardComponentType, DashboardImageToUpload, DashboardPrivacy } from '../models/dashboard.model';
import { DateTime } from 'luxon';
import { SimulatedEquipment } from '../../topology/models/simulatedEquipment.model';
import { CoreActions } from '@core/store';
import { CurrentSimulation } from '../../topology/models/current-simulation.model';
import { SimulationStatus, SimulationType } from '@core/models/simulation-metadata.model';

export const dashboardFeatureKey = 'dashboard';

export interface State {
  listLoaded: boolean;
  editionLoaded: boolean;
  list: boolean;
  toolbar: boolean;
  edition: boolean;
  dashboards: Dashboard[];
  dashboard: Dashboard;
  orders: string[];
  isTimeNavVisible: boolean;
  index: number;
  imagesToUpload: DashboardImageToUpload[];
  imagesToRemove: string[];
  currentSimulation: CurrentSimulation;
  simulatedEquipments: SimulatedEquipment[];
  simulatedGroups: SimulatedEquipment[];
  requestedDashboard: string;
}

const initialState: State = {
  listLoaded: false,
  editionLoaded: false,
  list: false,
  edition: false,
  toolbar: true,
  dashboards: [],
  dashboard: { name: '', width: 8, height: 6 } as Dashboard, // temp, should be null
  orders: [],
  isTimeNavVisible: true,
  index: 0,
  imagesToUpload: [],
  imagesToRemove: [],
  currentSimulation: { type: SimulationType.PRSIM, date: null, min: null, max: null, isLive: false },
  simulatedEquipments: [],
  simulatedGroups: [],
  requestedDashboard: ''
};

const orderDashboard = (dashboards: Dashboard[], orders): Dashboard[] => {
  const orderedDashboards = [];
  if (orders.length) {
    orders.forEach(order => {
      const orderedDashboard = dashboards.find(dash => dash.id === order);
      if (orderedDashboard) {
        orderedDashboards.push(orderedDashboard);
      }
    });
  }
  return [...dashboards.filter(dash => !orders.includes(dash.id)).sort(orderByCrationDate), ...orderedDashboards];
};

const orderByCrationDate = (a, b): number =>
  DateTime.fromISO(b.creationDate).toMillis() - DateTime.fromISO(a.creationDate).toMillis();

export const reducer = createReducer(
  initialState,
  on(DashboardActions.resetState, state => initialState),
  on(DashboardActions.listLoad, (state, { dashboards, orders, simulationType, date, minDate, maxDate, requested }) => {
    const allDashboards = orderDashboard(dashboards, orders);
    const index = requested ? allDashboards.findIndex(dash => dash.id === requested) : 0;
    const activeDate = state.currentSimulation.date || date;
    allDashboards[index] = { ...allDashboards[index], loaded: true };
    return {
      ...state,
      listLoaded: true,
      dashboards: allDashboards,
      dashboard: allDashboards[index],
      orders: orders,
      index,
      currentSimulation: {
        type: simulationType,
        date: activeDate,
        min: minDate,
        max: maxDate,
        isLive: activeDate === maxDate
      }
    };
  }),
  on(DashboardActions.requestDashboard, (state, { dashboardId }) => ({
    ...state,
    requestedDashboard: dashboardId
  })),
  on(DashboardActions.refreshDashboardListSuccess, (state, { dashboards, requested }) => {
    const allDashboards = orderDashboard(dashboards, state.orders);
    let index = requested ? allDashboards.findIndex(dash => dash.id === requested) : 0;
    // if requested no longer exist, force requested index to zero;
    index = Math.max(index, 0);
    allDashboards.forEach((dashboard, d) => {
      const existing = state.dashboards.find(dash => dash.id === dashboard.id);
      if (existing) {
        // only apply minimum information to the already present dashboards
        allDashboards[d] = { ...existing, privacy: allDashboards[d].privacy };
      }
    });
    return {
      ...state,
      dashboards: allDashboards,
      dashboard: allDashboards[index],
      index: index
    };
  }),
  on(DashboardActions.editionLoad, (state, { simulated }) => ({
    ...state,
    editionLoaded: true,
    simulatedEquipments: simulated
  })),
  on(DashboardActions.saveSimulatedGroups, (state, { simulatedGroups }) => ({
    ...state,
    simulatedGroups: simulatedGroups
  })),
  on(DashboardActions.toggleList, state => ({
    ...state,
    list: !state.list
  })),
  on(DashboardActions.toggleEdition, state => ({
    ...state,
    edition: true,
    toolbar: true,
    isTimeNavVisible: false
  })),
  on(DashboardActions.toggleToolbar, state => ({
    ...state,
    toolbar: !state.toolbar
  })),
  on(DashboardActions.openDashboard, (state, { index }) => ({
    ...state,
    dashboard: state.dashboards[index],
    index: index
  })),
  on(DashboardActions.loadDashboard, (state, { dashboard }) => ({
    ...state,
    dashboards: orderDashboard(
      [...state.dashboards.filter((d, index) => index !== state.index), dashboard],
      state.orders
    ),
    dashboard,
    index: state.dashboards.findIndex(dash => dash.id === dashboard.id)
  })),
  on(DashboardActions.openDashboardWithIdSuccess, (state, { dashboard, index }) => ({
    ...state,
    dashboards: orderDashboard(
      [...state.dashboards.filter((d, index) => index !== state.index), dashboard],
      state.orders
    ),
    dashboard: dashboard,
    index: index
  })),
  on(DashboardActions.createDashboard, state => {
    const dashboard = {
      name: 'Nouveau Monitoring',
      privacy: DashboardPrivacy.PRIVATE,
      width: 32,
      height: 16,
      components: [],
      isBlocksLocked: true,
      currentlyDraftBy: '',
      creationDate: new Date().toISOString()
    } as Dashboard;
    return {
      ...state,
      edition: true,
      list: false,
      dashboards: [dashboard, ...state.dashboards],
      dashboard: dashboard,
      index: 0,
      isTimeNavVisible: false
    };
  }),
  on(DashboardActions.removeDashboardSuccess, (state, { dashboardId }) => {
    const newDashboards = state.dashboards.filter(dashboard => dashboard.id !== dashboardId);
    const newEdition = state.dashboard?.id === dashboardId ? false : state.edition;

    return {
      ...state,
      edition: newEdition,
      list: !newEdition,
      isTimeNavVisible: !newEdition,
      dashboards: newDashboards,
      dashboard: state.dashboard?.id === dashboardId ? newDashboards[0] : state.dashboard,
      index:
        state.dashboard?.id === dashboardId
          ? 0
          : newDashboards.findIndex(dashboard => dashboard.id === state.dashboard.id)
    };
  }),
  on(DashboardActions.resetDashboard, state => ({
    ...state,
    edition: false,
    list: true,
    isTimeNavVisible: true,
    imagesToUpload: [],
    imagesToRemove: [],
    dashboard: state.dashboards.find((d, index) => index === state.index)
  })),
  on(DashboardActions.updateDashboard, (state, { dashboard }) => {
    const updatedDashboard = { ...state.dashboard, ...dashboard };
    return {
      ...state,
      dashboards: orderDashboard(
        [...state.dashboards.filter((d, index) => index !== state.index), updatedDashboard],
        state.orders
      ),
      dashboard: updatedDashboard
    };
  }),
  on(DashboardActions.updateDashboardOrderSuccess, (state, { orders }) => {
    const allDashboards = orderDashboard(state.dashboards, orders);
    const index = allDashboards.findIndex(dash => dash.id === state.dashboard.id);
    return {
      ...state,
      orders: orders,
      dashboards: allDashboards,
      index
    };
  }),
  on(DashboardActions.updateComponent, (state, { component }) => {
    const updatedDashboard = {
      ...state.dashboard,
      components: [...(state.dashboard.components?.filter(comp => comp.id !== component.id) || []), component]
    };
    return {
      ...state,
      dashboards: orderDashboard(
        [...state.dashboards.filter((d, index) => index !== state.index), updatedDashboard],
        state.orders
      ),
      dashboard: updatedDashboard
    };
  }),
  on(DashboardActions.removeComponent, (state, { componentId }) => {
    const updatedDashboard = {
      ...state.dashboard,
      components: [...state.dashboard.components?.filter(comp => comp.id !== componentId)]
    };
    return {
      ...state,
      imagesToRemove: [
        ...state.imagesToRemove,
        state.dashboard.components.find(component => component.id === componentId)?.image?.id
      ].filter(imageId => imageId),
      dashboards: orderDashboard(
        [...state.dashboards.filter((d, index) => index !== state.index), updatedDashboard],
        state.orders
      ),
      dashboard: updatedDashboard
    };
  }),
  on(DashboardActions.saveDashboardSuccess, (state, { dashboard }) => {
    const updatedDasboard = {
      ...state.dashboard,
      id: dashboard.id,
      creationDate: dashboard.creationDate,
      creatorEmail: dashboard.creatorEmail,
      creatorName: dashboard.creatorName,
      editionDate: dashboard.editionDate,
      components: [
        ...state.dashboard.components.map((comp, c) =>
          comp.type === DashboardComponentType.IMAGE ? dashboard.components[c] : comp
        )
      ]
    };
    return {
      ...state,
      edition: false,
      imagesToUpload: [],
      imagesToRemove: [],
      // no longer update data as preview already does it
      dashboards: orderDashboard(
        [...state.dashboards.filter((d, index) => index !== state.index), updatedDasboard],
        state.orders
      ),
      dashboard: updatedDasboard
    };
  }),
  on(DashboardActions.updateDashboardDate, (state, { date }) => {
    const updatedDate = DateTime.fromISO(date);

    if (
      updatedDate < DateTime.fromISO(state.currentSimulation.min) ||
      updatedDate > DateTime.fromISO(state.currentSimulation.max)
    ) {
      localStorage.removeItem('simulationDate');
      return {
        ...state,
        currentSimulation: {
          ...state.currentSimulation,
          isLive: true,
          date: state.currentSimulation.max
        }
      };
    } else {
      const isLive = DateTime.fromISO(state.currentSimulation.max).equals(updatedDate);
      if (isLive) {
        localStorage.removeItem('simulationDate');
      } else {
        localStorage.setItem('simulationDate', updatedDate.toISO());
      }
      return {
        ...state,
        currentSimulation: {
          ...state.currentSimulation,
          isLive,
          date
        }
      };
    }
  }),
  on(DashboardActions.setTimeNavVisibility, (state, { data }) => ({
    ...state,
    isTimeNavVisible: data
  })),
  on(DashboardActions.reverseTimeNavVisibility, state => ({
    ...state,
    isTimeNavVisible: !state.isTimeNavVisible
  })),
  on(DashboardActions.saveDashboard, state => ({
    ...state,
    list: true,
    isTimeNavVisible: true
  })),
  on(DashboardActions.addInImagesToUpload, (state, { imagesToUpload }) => ({
    ...state,
    imagesToUpload: [...state.imagesToUpload, imagesToUpload]
  })),
  on(DashboardActions.loadDashboardImages, (state, { dashboard }) => ({
    ...state,
    imagesToUpload: [],
    imagesToRemove: [],
    dashboards: orderDashboard(
      [...state.dashboards.filter((d, index) => index !== state.index), dashboard],
      state.orders
    ),
    dashboard
  })),
  // time navigation header
  on(CoreActions.updateLatestSimulationResult, (state, { simulationResult }) => {
    if (
      simulationResult.simulationType === state.currentSimulation.type &&
      simulationResult.status === SimulationStatus.DONE
    ) {
      return {
        ...state,
        currentSimulation: {
          ...state.currentSimulation,
          max: simulationResult.date
        }
      };
    } else {
      return state;
    }
  })
);
